import React from 'react';
import {
  Badge,
  DataCell,
  Flex,
  HoverCardContent,
  HoverCardRoot,
  HoverCardTrigger,
  Text,
  Tooltip,
} from '@/components/ui';
import { DataList } from '@/components/ui/DataList';
import { getLastImportedVersionInfo } from '@/utils/csvFileUtils';
import { CsvFile } from '@/types/csvFiles';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface VersionInfoCellProps {
  value?: unknown;
  file?: CsvFile;
  render?: () => React.ReactNode;
}

function VersionInfoCell({ file, render }: VersionInfoCellProps) {
  if (render) {
    return <>{render()}</>;
  }

  if (!file) {
    return (
      <Text size="2" color="gray">
        -
      </Text>
    );
  }

  const versionInfo = getLastImportedVersionInfo(file);

  // If no imported version info, show a clear label
  if (!versionInfo) {
    return (
      <Text size="2" color="gray">
        Not imported
      </Text>
    );
  }

  const { versionNumber, importedAt, importedBy } = versionInfo;

  const isLatestImported = Boolean(
    file.latest_imported_version &&
      (file.last_version_id === file.latest_imported_version.id ||
        (file.last_version?.order_index !== undefined &&
          file.latest_imported_version.order_index ===
            file.last_version.order_index))
  );

  // If there's no additional info, just show version number
  if (!importedAt && !importedBy) {
    return (
      <Flex align="center" gap="2">
        <Text size="2" color="gray">
          {versionNumber}
        </Text>
        {isLatestImported && (
          <Badge size="xsmall" color="success" variant="surface">
            Up to date
          </Badge>
        )}
      </Flex>
    );
  }

  // Show version number with hover card for full info
  return (
    <Flex align="center" gap="2">
      <HoverCardRoot>
        <HoverCardTrigger>
          <Text
            size="2xsmall"
            color={isLatestImported ? 'textPrimary' : 'default'}
            weight={isLatestImported ? 'bold' : 'regular'}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
              textUnderlineOffset: '2px',
            }}
          >
            {versionNumber}
          </Text>
        </HoverCardTrigger>
        <HoverCardContent size="2">
          <Flex direction="column" gap="3">
            <Text size="xsmall" weight="medium">
              Version Information
            </Text>
            <DataList.Root
              style={{
                gap: 'var(--space-4) var(--space-2)',
              }}
            >
              <DataList.Item>
                <DataList.Label>Version</DataList.Label>
                <DataList.Value>{versionNumber}</DataList.Value>
              </DataList.Item>
              {importedAt && (
                <DataList.Item>
                  <DataList.Label>Imported</DataList.Label>
                  <DataList.Value>
                    <DataCell type="datetime" value={importedAt} />
                  </DataList.Value>
                </DataList.Item>
              )}
              {importedBy && (
                <DataList.Item>
                  <DataList.Label>Imported By</DataList.Label>
                  <DataList.Value>
                    <DataCell type="user" value={importedBy} />
                  </DataList.Value>
                </DataList.Item>
              )}
            </DataList.Root>
          </Flex>
        </HoverCardContent>
      </HoverCardRoot>
      {isLatestImported && (
        <Tooltip content="Up to date">
          <Badge
            size="xsmall"
            color="success"
            variant="surface"
            // startIcon={<IconDatabaseImport style={{ color: 'var(--green-9)' }} />}
          >
            {/*<IconDatabaseImport style={{ color: 'var(--green-9)' }} />*/}
            Current
          </Badge>
        </Tooltip>
      )}
    </Flex>
  );
}

export default VersionInfoCell;

export function VersionInfoCellSkeleton() {
  return (
    <div className="data-cell__version-info-skeleton">
      <div className="skeleton skeleton--text" style={{ width: '60px' }} />
    </div>
  );
}
